import { useMemo } from 'react';

import { Checkbox, Collapse, Icon } from '@atom/mui';
import { DraftElement } from '@atom/types/ai';
import { AssetCreateAttributesInput } from '@atom/types/inventory';
import {
  isAncestorInSet,
  mapWorkflowAttributesToAttributes,
} from '@atom/utilities/assetDraftUtilities';
import { getSchemaTreeMap } from '@atom/utilities/schemaUtilities';

import ChatAssetDraftAttributes from './ChatAssetDraftAttributes';
import { useChatAssetDraftContext } from './ChatAssetDraftContext';
import ChatAssetDraftElements from './ChatAssetDraftElements';

import classes from '../chat.module.css';

type ChatAssetDraftElementRowProps = {
  element: DraftElement;
  path: (string | number)[];
};

const EMPTY_ERRORS: Set<string> = new Set();

const ChatAssetDraftElementRow = ({
  element,
  path,
}: ChatAssetDraftElementRowProps) => {
  const {
    schema: rootSchema,
    disabled,
    expandedPaths,
    excludedPaths,
    errorsByPath,
    handleAttributeChange,
    handleToggleExpanded,
    handleToggleExcluded,
  } = useChatAssetDraftContext();

  const schema = useMemo(
    () => getSchemaTreeMap(rootSchema).get(element.schemaId) ?? null,
    [rootSchema, element.schemaId],
  );

  const pathKey = path.join('.');
  const expanded = expandedPaths.has(pathKey);
  const selfExcluded = excludedPaths.has(pathKey);
  const ancestorExcluded = isAncestorInSet(pathKey, excludedPaths);
  const excluded = selfExcluded || ancestorExcluded;
  const errorAttributeIds = errorsByPath.get(pathKey) ?? EMPTY_ERRORS;

  const attributes = useMemo<AssetCreateAttributesInput>(
    () => mapWorkflowAttributesToAttributes(element.attributes),
    [element.attributes],
  );

  const children = element.elements ?? [];

  return (
    <div className={classes.assetDraftCardElementRow}>
      <div className={classes.assetDraftCardElementHeader}>
        <button
          type="button"
          className={classes.assetDraftCardElementToggle}
          onClick={() => handleToggleExpanded(pathKey)}
        >
          <Icon className={classes.assetDraftCardElementIcon}>
            {expanded ? 'expand_less' : 'expand_more'}
          </Icon>
          <span className={classes.assetDraftCardElementName}>
            {element.name}
          </span>
        </button>
        <Checkbox
          checked={!excluded}
          onChange={() => handleToggleExcluded(pathKey)}
          disabled={disabled || ancestorExcluded}
        />
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className={classes.assetDraftCardElementBody}>
          {schema && (
            <ChatAssetDraftAttributes
              schema={schema}
              workflowAttributes={element.attributes}
              attributes={attributes}
              onAttributeChange={(id, value) =>
                handleAttributeChange(path, id, value)
              }
              errorAttributeIds={errorAttributeIds}
              disabled={disabled || excluded}
            />
          )}
          {children.length > 0 && (
            <div className={classes.assetDraftCardElementChildren}>
              <ChatAssetDraftElements elements={children} path={path} />
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default ChatAssetDraftElementRow;
